import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users, Plus, Edit, Trash2, Search, X, Save, Image as ImageIcon, 
  Loader2, Check, MapPin, Facebook, Upload
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

function TeamForm({ team, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: team?.name || "",
    short_name: team?.short_name || "",
    city: team?.city || "",
    country: team?.country || "Honduras",
    logo_url: team?.logo_url || "",
    facebook_url: team?.facebook_url || "",
    division: team?.division || "primera",
    is_active: team?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchMedia = async () => {
    setLoadingMedia(true);
    try {
      const response = await axios.get(`${API}/media?limit=50`);
      setMediaList(response.data.items || []);
    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Solo se permiten imágenes");
      return;
    }
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      const response = await axios.post(`${API}/media/upload`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.url) {
        // Set the uploaded image as the logo
        setFormData({ ...formData, logo_url: response.data.url });
        setShowMediaPicker(false);
        toast.success("Logo subido correctamente");
        // Refresh media list
        fetchMedia();
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("El nombre es obligatorio");
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      toast.error("Error al guardar el equipo");
    } finally {
      setSaving(false);
    }
  };

  const selectImage = (media) => {
    setFormData({ ...formData, logo_url: media.url });
    setShowMediaPicker(false);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo */}
        <div>
          <Label>Logo del Equipo</Label>
          <div className="mt-2 flex items-start gap-4">
            <div 
              className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary transition-colors"
              onClick={() => { fetchMedia(); setShowMediaPicker(true); }}
            >
              {formData.logo_url ? (
                <img src={getImageUrl(formData.logo_url)} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <div className="text-center p-2">
                  <ImageIcon className="h-8 w-8 text-gray-400 mx-auto" />
                  <span className="text-xs text-gray-500">Logo</span>
                </div>
              )}
            </div>
            {formData.logo_url && (
              <Button type="button" variant="outline" size="sm" onClick={() => setFormData({ ...formData, logo_url: "" })}>
                <X className="h-4 w-4 mr-1" /> Quitar
              </Button>
            )}
          </div>
        </div>

        {/* Name */}
        <div>
          <Label>Nombre del Equipo *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ej: Club Deportivo Marathón"
            className="mt-1"
            required
          />
        </div>

        {/* Short Name */}
        <div>
          <Label>Siglas / Nombre Corto</Label>
          <Input
            value={formData.short_name}
            onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
            placeholder="Ej: MAR"
            className="mt-1"
            maxLength={5}
          />
        </div>

        {/* City & Country */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Ciudad</Label>
            <Input
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              placeholder="Ej: San Pedro Sula"
              className="mt-1"
            />
          </div>
          <div>
            <Label>País</Label>
            <Input
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              placeholder="Honduras"
              className="mt-1"
            />
          </div>
        </div>

        {/* Division */}
        <div>
          <Label>División *</Label>
          <Select value={formData.division} onValueChange={(v) => setFormData({ ...formData, division: v })}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Seleccionar división" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="primera">Primera División</SelectItem>
              <SelectItem value="segunda">Segunda División</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Facebook URL */}
        <div>
          <Label className="flex items-center gap-2">
            <Facebook className="h-4 w-4 text-blue-600" />
            Página de Facebook
          </Label>
          <Input
            value={formData.facebook_url}
            onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
            placeholder="https://facebook.com/clubmarathon"
            className="mt-1"
            type="url"
          />
          <p className="text-xs text-gray-500 mt-1">URL completa de la página oficial del equipo</p>
        </div>

        {/* Active */}
        <div className="flex items-center justify-between py-4 border-t">
          <div>
            <Label>Estado</Label>
            <p className="text-sm text-gray-500">¿El equipo está activo?</p>
          </div>
          <Switch
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={saving}>
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Guardando...</> : <><Save className="h-4 w-4 mr-2" />Guardar</>}
          </Button>
        </div>
      </form>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Seleccionar o Subir Logo</DialogTitle>
          </DialogHeader>
          
          {/* Upload Section */}
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 mb-4 text-center hover:border-primary transition-colors">
            <input
              type="file"
              id="logo-upload"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
            <label htmlFor="logo-upload" className="cursor-pointer block">
              {uploading ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="h-10 w-10 animate-spin text-primary mb-2" />
                  <span className="text-sm text-gray-600">Subiendo imagen...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="h-10 w-10 text-gray-400 mb-2" />
                  <span className="text-sm font-medium text-gray-700">Subir nueva imagen</span>
                  <span className="text-xs text-gray-500 mt-1">PNG, JPG, WEBP hasta 10MB</span>
                </div>
              )}
            </label>
          </div>
          
          {/* Separator */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 border-t border-gray-200"></div>
            <span className="text-sm text-gray-500">o selecciona una imagen existente</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>
          
          {/* Media Grid */}
          {loadingMedia ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : mediaList.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ImageIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No hay imágenes en la biblioteca</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3">
              {mediaList.map((media) => (
                <div
                  key={media.media_id}
                  className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all bg-gray-100"
                  onClick={() => selectImage(media)}
                >
                  <img src={getImageUrl(media.url)} alt={media.title || "Media"} className="w-full h-full object-contain" />
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AdminEquipos() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [divisionFilter, setDivisionFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  useEffect(() => {
    fetchTeams();
  }, [page, limit]);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/sports/teams?skip=${(page - 1) * limit}&limit=${limit}`);
      setTeams(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    if (editingTeam) {
      await axios.put(`${API}/sports/teams/${editingTeam.team_id}`, data);
      toast.success("Equipo actualizado");
    } else {
      await axios.post(`${API}/sports/teams`, data);
      toast.success("Equipo creado");
    }
    fetchTeams();
    setEditingTeam(null);
    setShowForm(false);
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm("¿Eliminar este equipo?")) return;
    try {
      await axios.delete(`${API}/sports/teams/${teamId}`);
      toast.success("Equipo eliminado");
      fetchTeams();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const filteredTeams = teams.filter((t) => {
    const matchesSearch = t.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDivision = divisionFilter === "all" || t.division === divisionFilter;
    return matchesSearch && matchesDivision;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-text flex items-center gap-2">
              <Users className="h-6 w-6 text-primary" />
              Equipos
            </h1>
            <p className="text-brand-textSecondary">Gestiona los equipos de fútbol</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Buscar equipos..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
            <Select value={divisionFilter} onValueChange={setDivisionFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="División" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las divisiones</SelectItem>
                <SelectItem value="primera">Primera División</SelectItem>
                <SelectItem value="segunda">Segunda División</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => { setEditingTeam(null); setShowForm(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Equipo
          </Button>
        </div>

        {/* Teams Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredTeams.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No hay equipos</h3>
            <Button className="mt-4" onClick={() => { setEditingTeam(null); setShowForm(true); }}>
              <Plus className="h-4 w-4 mr-2" />Agregar Equipo
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTeams.map((team) => (
              <div key={team.team_id} className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  {team.logo_url ? (
                    <img src={getImageUrl(team.logo_url)} alt={team.name} className="w-16 h-16 object-contain" />
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Users className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="font-medium text-brand-text">{team.name}</h3>
                    {team.short_name && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{team.short_name}</span>}
                    {team.city && <p className="text-sm text-gray-500 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" />{team.city}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${team.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {team.is_active ? "Activo" : "Inactivo"}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${team.division === "primera" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>
                      {team.division === "primera" ? "1ra Div" : "2da Div"}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditingTeam(team); setShowForm(true); }}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(team.team_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Form Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingTeam ? "Editar Equipo" : "Nuevo Equipo"}</DialogTitle>
            </DialogHeader>
            <TeamForm team={editingTeam} onSave={handleSave} onClose={() => { setShowForm(false); setEditingTeam(null); }} />
          </DialogContent>
        </Dialog>
      </div>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="equipos-pagination"
          label="equipos"
        />
    </AdminLayout>
  );
}
